import React from 'react';
import { MapPin, Sun, Wind, Calendar } from 'lucide-react';

const MasaiMara = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section
        className="relative h-96 flex items-center justify-center text-white"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1547471080-7cc2caa01a7e?w=1200)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0" style={{ backgroundColor: 'rgba(101, 67, 33, 0.5)' }}></div>
        <div className="relative z-10 text-center px-4">
          <h1 className="text-5xl md:text-6xl font-bold mb-4">Uganda</h1>
          <p className="text-xl">Africa's Greatest Wildlife Theatre</p>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-gray-800 mb-8">Experience Essential Africa</h2>
          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            A place unchanged by the passage of time, Uganda is essential Africa. 
            Everyone who travels here says that 'Africa gets into your blood'. It's so true! 
            Rugged and dramatic, this is a wilderness of emotion, survival and inspiration.
          </p>
          <p className="text-lg text-gray-700 leading-relaxed">
            Wildlife drama unravels before you at every turn. Often raw and sometimes violent, 
            this wildlife theatre is there before your very eyes. There is nowhere else like it!
          </p>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 px-4 bg-stone-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-800 mb-12 text-center">Why Fly Over the Mara?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-md">
              <MapPin className="w-12 h-12 mb-4" style={{ color: '#EE6A1F' }} />
              <h3 className="text-2xl font-bold text-gray-800 mb-3">Unparalleled Views</h3>
              <p className="text-gray-600 leading-relaxed">
                See the vast expanse of the Mara from above - wildlife patterns, river systems, 
                and the endless savannah stretching to the horizon. A perspective impossible from the ground.
              </p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-md">
              <Sun className="w-12 h-12 mb-4" style={{ color: '#EE6A1F' }} />
              <h3 className="text-2xl font-bold text-gray-800 mb-3">Sunrise Magic</h3>
              <p className="text-gray-600 leading-relaxed">
                Experience the African sunrise from the sky. Watch as golden light floods the plains, 
                illuminating wildlife below in one of nature's most spectacular daily shows.
              </p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-md">
              <Wind className="w-12 h-12 mb-4" style={{ color: '#EE6A1F' }} />
              <h3 className="text-2xl font-bold text-gray-800 mb-3">Peaceful Flight</h3>
              <p className="text-gray-600 leading-relaxed">
                Drift silently over the landscape. Unlike vehicles, hot air balloons move with the wind, 
                creating a serene experience that doesn't disturb the wildlife below.
              </p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-md">
              <Calendar className="w-12 h-12 mb-4" style={{ color: '#EE6A1F' }} />
              <h3 className="text-2xl font-bold text-gray-800 mb-3">Great Migration</h3>
              <p className="text-gray-600 leading-relaxed">
                Witness the Great Migration from above - millions of wildebeest and zebra moving across 
                the plains. An aerial view reveals the true scale of this natural wonder.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Landscape Section */}
      <section
        className="relative py-32 px-4 text-white text-center"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1516426122078-c23e76319801?w=1200)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      >
        <div className="absolute inset-0 bg-black/40"></div>
        <div className="relative z-10 max-w-4xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">A Wilderness Like No Other</h2>
          <p className="text-xl italic">
            "Uganda from above - absolutely breathtaking. 
            You see patterns and movements you never knew existed."
          </p>
          <p className="text-sm mt-4">- Trip Advisor Review, 2024</p>
        </div>
      </section>

      {/* Wildlife Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">What You'll See From Above</h2>
          <div className="space-y-6 text-gray-700 leading-relaxed">
            <p>
              From your vantage point in the sky, Uganda reveals itself as a living tapestry. 
              Herds of elephants move in family groups, their paths creating trails across the golden grass. 
              Lions rest in the shade of acacia trees, their presence only visible from above.
            </p>
            <p>
              The Mara River winds through the landscape like a silver ribbon, its banks teeming with 
              hippos and crocodiles. During migration season, you'll witness thousands of wildebeest 
              gathering at crossing points, preparing for their perilous journey across the water.
            </p>
            <p>
              Giraffes browse among the treetops, nearly at eye level with your balloon. Buffalo herds 
              scatter patterns across the plains. And if you're fortunate, you might spot cheetahs on the 
              hunt or a leopard resting in the branches of a fever tree.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 text-white text-center" style={{ backgroundColor: '#EE6A1F' }}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold mb-8">Ready to Soar Over the Mara?</h2>
          <p className="text-xl mb-8">Experience the adventure of a lifetime with SKYHIGH Adventures</p>
          <a
            href="/contact"
            className="inline-block font-bold px-10 py-4 rounded text-lg transition-transform bg-[#EE6A1F] text-white border border-white/60 shadow-lg hover:scale-105"
          >
            BOOK YOUR FLIGHT
          </a>
        </div>
      </section>
    </div>
  );
};

export default MasaiMara;
